import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaBell, FaPen, FaPlus, FaTimes, FaTrash } from 'react-icons/fa';
import ErpTemplate from '../../components/ErpTemplate';
import { FinCheck, FinField, FinInput, FinTextarea } from '../../components/FinField';
import ReactSelect from '../../components/ReactSelect';
import api from '../../services/api';
import notify from '../../services/notify';
import { TIPOS_EVENTO, rotuloCliente } from './eventoTipos';
import '../../css/financeiro-parcela-pagar.css';

const CATEGORIAS = [
    { value: 'cliente', label: 'Cliente' },
    { value: 'access', label: 'Access' },
    { value: 'financeiro', label: 'Financeiro' },
];

// 'todos' marca 'vale para qualquer valor' — nunca NULL, porque o índice único do MySQL não
// compara NULL entre si e duas regras genéricas idênticas passariam batido.
const TODOS = 'todos';

const BACKOFFICES = [
    { value: TODOS, label: 'Todos' },
    { value: 'terceiro', label: 'Terceiro' },
    { value: 'exclusivo', label: 'Exclusivo' },
];

const COMPLEXIDADES = [
    { value: TODOS, label: 'Todas' },
    { value: 'baixa', label: 'Baixa' },
    { value: 'media', label: 'Média' },
    { value: 'alta', label: 'Alta' },
];

const CONTAGENS = [
    { value: 'corridos', label: 'Dias corridos' },
    { value: 'uteis', label: 'Dias úteis' },
];

const ALERTA_VAZIO = {
    nome: '',
    gatilho: '',
    offset_dias: 0,
    contagem: 'corridos',
    pendencias: [],
    texto: '',
    ativo: false,
};

// Os três tipos de destinatário. O rótulo é o que aparece no menu do botão; o `controle` decide
// se a linha ganha um select ou um campo de texto.
const TIPOS_DESTINATARIO = [
    { value: 'perfil', label: 'Cargo/Perfil', controle: 'select' },
    { value: 'usuario', label: 'Usuário', controle: 'select' },
    { value: 'email', label: 'E-mail fixo', controle: 'texto' },
];

const rotuloTipoDestinatario = (tipo) => TIPOS_DESTINATARIO.find((t) => t.value === tipo)?.label ?? tipo;

/**
 * O id de uma relação, venha ela como escalar ou como objeto.
 *
 * O Laravel tem $snakeAttributes = true, então a relação `Gatilho()` é serializada com a MESMA
 * chave da coluna FK (`gatilho`) e sobrescreve o id: quando o endpoint faz eager load, o campo vem
 * objeto; quando não faz, vem escalar. `alerta.Gatilho` maiúsculo nunca existe no JSON.
 *
 * Sem isto, `String(campo)` estoura "Cannot convert object to primitive value" e derruba a tela.
 */
const idDaRelacao = (valor) => {
    if (valor && typeof valor === 'object') {
        return valor.id != null ? String(valor.id) : '';
    }

    return valor != null ? String(valor) : '';
};

/** O nome de uma relação, quando ela veio carregada. */
const nomeDaRelacao = (valor) => (valor && typeof valor === 'object' ? (valor.nome ?? '') : '');

export default function CadastroSla({ match }) {
    const id = match.params.id;
    const token = localStorage.getItem('token');
    const history = useHistory();

    const [tabState, setTabState] = useState('geral');
    const [dados, setDados] = useState({ categoria: 'cliente', backoffice: TODOS, tipo: TODOS, complexidade: TODOS, ativo: true });
    const [alertas, setAlertas] = useState([]);
    const [criterios, setCriterios] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(!!id);

    // Modal do alerta: null = fechado; objeto = editando (com id) ou criando (sem id).
    const [alertaEdicao, setAlertaEdicao] = useState(null);
    const [salvandoAlerta, setSalvandoAlerta] = useState(false);

    // Perfis vêm do banco, não de lista fixa: o vocabulário é por tenant e muda sem deploy.
    const [perfis, setPerfis] = useState([]);
    // O menu do botão "Adicionar destinatário" — fechado, ele parece um botão comum.
    const [menuDestinatario, setMenuDestinatario] = useState(false);

    const buscarPerfis = () => {
        api({ method: 'get', action: '/rhPerfil', token })
            .then((response) => setPerfis(
                (response.data.data ?? []).map((item) => ({ value: item.codigo, label: item.nome }))
            ))
            .catch((error) => {
                setPerfis([]);
                toast.error('Erro ao carregar os perfis: ' + (error.response?.data?.error ?? error.message ?? ''));
            });
    };

    // Usuário é busca assíncrona: a base tem milhares de participantes, lista fixa não serve.
    const buscarUsuarios = (input) => api({
        method: 'get',
        action: '/participante',
        token,
        params: { nome: input, tipoCadastro: 'pessoa', situacaoRegistro: 'ativo' },
    })
        .then((response) => (response.data.data ?? []).map((registro) => ({
            value: String(registro.id),
            label: registro.nome + (registro.email ? ' - ' + registro.email : ''),
        })))
        .catch(() => []);

    const adicionarDestinatario = (tipo) => {
        setMenuDestinatario(false);
        setAlertaEdicao((state) => ({
            ...state,
            destinatarios: [...(state.destinatarios ?? []), { tipo, valor: '' }],
        }));
    };

    // nomeUsuario guarda o label só para a tela reexibir o nome sem uma segunda busca — não sobe
    // no payload, que leva apenas tipo e valor.
    const alterarDestinatario = (indice, valor, nomeUsuario) => {
        setAlertaEdicao((state) => {
            const lista = [...(state.destinatarios ?? [])];
            lista[indice] = { ...lista[indice], valor, ...(nomeUsuario ? { nomeUsuario } : {}) };

            return { ...state, destinatarios: lista };
        });
    };

    const removerDestinatario = (indice) => {
        setAlertaEdicao((state) => ({
            ...state,
            destinatarios: (state.destinatarios ?? []).filter((_, i) => i !== indice),
        }));
    };

    const criteriosDe = (tipo) => criterios
        .filter((item) => item.tipo === tipo)
        .map((item) => ({ value: String(item.id), label: item.nome }));

    const rotuloCriterio = (criterioId) => {
        const alvo = idDaRelacao(criterioId);
        const achado = criterios.find((item) => String(item.id) === alvo);
        return achado ? achado.nome : '';
    };

    const buscar = () => {
        if (!id) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        api({ method: 'get', action: `/sla/${id}`, token })
            .then((response) => {
                const recebido = response.data.data ?? {};
                setAlertas(recebido.alertas ?? []);
                const semAlertas = { ...recebido };
                delete semAlertas.alertas;
                // $snakeAttributes faz a relação vir na MESMA chave da FK, então `cliente` chega
                // objeto quando houve eager load. Normaliza aqui: o estado guarda o id, e o nome
                // vai para nomeCliente — sem isto o payload do salvar levaria o objeto inteiro.
                semAlertas.modeloCliente = recebido.cliente?.modelo ?? 'master';
                semAlertas.nomeCliente = nomeDaRelacao(recebido.cliente) || semAlertas.nomeCliente || '';
                semAlertas.cliente = idDaRelacao(recebido.cliente) || null;
                setDados(semAlertas);
            })
            .catch((error) => {
                // Sem isto a tela abre em branco como se fosse um cadastro novo, e o operador
                // cadastra duplicado achando que o registro sumiu.
                toast.error('Erro ao carregar o SLA: ' + (error.response?.data?.error ?? error.message ?? ''));
            })
            .finally(() => setIsLoading(false));
    };

    const buscarCriterios = () => {
        api({ method: 'get', action: '/slaCriterio/opcoes', token })
            .then((response) => setCriterios(response.data.data ?? []))
            .catch((error) => {
                // Sem isto os selects de Gatilho e Pendência ficam "Sem opções" e o alerta vira
                // impossível de salvar, sem nenhuma pista de que a falha foi no carregamento.
                setCriterios([]);
                toast.error('Erro ao carregar os critérios: ' + (error.response?.data?.error ?? error.message ?? ''));
            });
    };

    const salvar = () => {
        if (isSubmitting) return;

        if (!dados.cliente) {
            toast.error('Selecione o cliente.');
            setTabState('geral');
            return;
        }
        if (!dados.descricao) {
            toast.error('Informe a descrição.');
            setTabState('geral');
            return;
        }

        setIsSubmitting(true);

        const payload = {
            cliente: dados.cliente,
            categoria: dados.categoria,
            backoffice: dados.backoffice || TODOS,
            tipo: dados.tipo || TODOS,
            complexidade: dados.complexidade || TODOS,
            descricao: dados.descricao,
            ativo: !!dados.ativo,
        };

        const requisicao = id
            ? { method: 'put', action: `/sla/${id}`, data: payload }
            : { method: 'post', action: '/sla', data: payload };

        api({ ...requisicao, token })
            .then((response) => {
                toast.success('Dados salvos com sucesso!');
                // SLA novo: fica na tela com o id, para o operador já cadastrar os alertas.
                if (!id) {
                    const novoId = response.data.data?.id;
                    if (novoId) {
                        history.replace(`/cadastroSla/${novoId}`);
                        return;
                    }
                }
                history.push('/sla');
            })
            .catch((error) => {
                notify.apiError(error, 'Erro ao salvar.');
            })
            .finally(() => setIsSubmitting(false));
    };

    const salvarAlerta = () => {
        if (salvandoAlerta || !alertaEdicao) return;

        if (!alertaEdicao.nome || !alertaEdicao.gatilho || !(alertaEdicao.pendencias ?? []).length
            || !alertaEdicao.texto) {
            toast.error('Preencha nome, gatilho, ao menos uma pendência e texto.');
            return;
        }

        // Linha em branco é destinatário que o operador abriu e não escolheu — não vale como
        // preenchido, senão o alerta sobe sem ninguém para receber.
        const destinatarios = (alertaEdicao.destinatarios ?? [])
            .filter((d) => d.tipo && String(d.valor ?? '').trim() !== '')
            .map((d) => ({ tipo: d.tipo, valor: String(d.valor).trim() }));

        if (destinatarios.length === 0) {
            toast.error('Adicione ao menos um destinatário.');
            return;
        }

        setSalvandoAlerta(true);

        // Payload montado campo a campo, e não por spread do alertaEdicao: ele carrega o registro
        // inteiro que o GET devolveu (created_at, updated_at, sla e os objetos de relação), e
        // reenviar isso num PUT é o padrão de escrita stale que já apagou dado em outras telas.
        const alertaId = alertaEdicao.id;
        const payload = Object.keys(ALERTA_VAZIO).reduce((acc, campo) => {
            acc[campo] = alertaEdicao[campo] ?? ALERTA_VAZIO[campo];
            return acc;
        }, {});

        payload.offset_dias = Number(alertaEdicao.offset_dias ?? 0);
        payload.destinatarios = destinatarios;

        const requisicao = alertaId
            ? { method: 'put', action: `/sla/${id}/alerta/${alertaId}`, data: payload }
            : { method: 'post', action: `/sla/${id}/alerta`, data: payload };

        api({ ...requisicao, token })
            .then(() => {
                toast.success('Alerta salvo!');
                setAlertaEdicao(null);
                buscar();
            })
            .catch((error) => {
                notify.apiError(error, 'Erro ao salvar alerta.');
            })
            .finally(() => setSalvandoAlerta(false));
    };

    const excluirAlerta = (alerta) => {
        if (!window.confirm(`Excluir o alerta "${alerta.nome}"?`)) return;

        api({ method: 'delete', action: `/sla/${id}/alerta/${alerta.id}`, token })
            .then(() => { toast.success('Alerta excluído.'); buscar(); })
            .catch((error) => toast.error('Erro ao excluir: ' + (error.response?.data?.error ?? error.message ?? '')));
    };

    useEffect(() => {
        buscarCriterios();
        buscarPerfis();
        buscar();
        // eslint-disable-next-line
    }, []);

    // O prazo tem sinal: negativo avisa antes da âncora, positivo cobra depois.
    const descreverPrazo = (alerta) => {
        const dias = Number(alerta.offset_dias ?? 0);
        const unidade = alerta.contagem === 'uteis' ? 'dias úteis' : 'dias corridos';
        if (dias === 0) return 'na data';
        return dias < 0
            ? `${Math.abs(dias)} ${unidade} antes`
            : `${dias} ${unidade} depois`;
    };

    return (
        <ErpTemplate processando={isLoading}>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">SLA</h1>
                        <p className="fin-pp__subtitle">{dados.descricao || 'Novo SLA'}</p>
                    </div>
                    <div className="fin-pp__actions">
                        <button type="button" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm" onClick={() => history.push('/sla')}>
                            Voltar
                        </button>
                        <button type="button" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--pill" onClick={salvar} disabled={isSubmitting}>
                            {isSubmitting ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>

                <form className="form" onSubmit={(e) => { salvar(); e.preventDefault(); }}>
                    <div className="fin-pp__tabs mb-3">
                        {[
                            { key: 'geral', label: 'Geral' },
                            { key: 'alertas', label: `Alertas${alertas.length ? ` (${alertas.length})` : ''}` },
                        ].map((item) => (
                            <button
                                key={item.key}
                                type="button"
                                className={'fin-pp__tab' + (tabState === item.key ? ' is-active' : '')}
                                onClick={() => setTabState(item.key)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    {tabState === 'geral' && (
                        <>
                            <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                                <div className="fin-pp__cphead">
                                    <span className="fin-pp__cphead-title">Partição</span>
                                    <span className="fin-pp__cphead-sub">Igualdade estrita — nunca "todos".</span>
                                </div>
                                <div className="fin-pp__filters">
                                    <FinField label="Cliente" required grow>
                                        <ReactSelect
                                            // nomeCliente PRIMEIRO: ele é o que o onChange atualiza.
                                            // Na ordem inversa, o objeto Cliente que veio do GET
                                            // continuaria vencendo depois da troca, e o select
                                            // mostraria o cliente ANTIGO enquanto o salvar manda o novo.
                                            value={dados.cliente ? { value: dados.cliente, label: dados.nomeCliente ?? '' } : null}
                                            // Trocar de cliente zera o backoffice: um valor escolhido para a Sanofi
                                            // ficaria salvo em cliente que nem tem esse eixo, e a regra passaria a
                                            // casar zero eventos em silêncio.
                                            onChange={(e) => setDados({
                                                ...dados,
                                                cliente: e?.value ?? null,
                                                nomeCliente: e?.label ?? '',
                                                modeloCliente: e?.modelo ?? 'master',
                                            })}
                                            loadOptions={(input) => api({
                                                method: 'get',
                                                action: '/participante',
                                                token,
                                                params: { nome: input, tipoCadastro: 'cliente', situacaoRegistro: 'ativo' }
                                            }).then((response) => (response.data.data ?? []).map((registro) => ({
                                                value: registro.id,
                                                label: rotuloCliente(registro),
                                                // Vem junto para decidir o eixo backoffice sem uma segunda requisição.
                                                modelo: registro.modelo ?? 'master'
                                            }))).catch(() => [])}
                                        />
                                    </FinField>
                                    <FinField label="Categoria" required>
                                        <ReactSelect
                                            value={CATEGORIAS.find((item) => item.value === dados.categoria) ?? null}
                                            onChange={(e) => setDados({ ...dados, categoria: e?.value ?? '' })}
                                            options={CATEGORIAS}
                                        />
                                    </FinField>
                                </div>
                            </div>

                            <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                                <div className="fin-pp__cphead">
                                    <span className="fin-pp__cphead-title">Abrangência</span>
                                    <span className="fin-pp__cphead-sub">"Todos" vale para qualquer valor. Entre os que casam, o mais específico vence.</span>
                                </div>
                                <div className="fin-pp__filters">
                                    <FinField label="Backoffice">
                                        {/* Só a família Sanofi separa terceiro de exclusivo. Quem decide é o
                                            BACKEND (SlaController::validar), que resolve a herança de modelo —
                                            opella e medley também caem em sanofi — e força 'todos' fora dela,
                                            sem recusar. Repetir a regra aqui pela coluna crua desabilitava o
                                            campo justamente para Opella/Medley, que o backend aceitaria. */}
                                        <ReactSelect
                                            value={BACKOFFICES.find((item) => item.value === (dados.backoffice ?? TODOS)) ?? null}
                                            onChange={(e) => setDados({ ...dados, backoffice: e?.value ?? TODOS })}
                                            options={BACKOFFICES}
                                        />
                                    </FinField>
                                    <FinField label="Tipo de evento">
                                        {/* Lista fechada, não campo livre: o casamento é igualdade
                                            estrita de string, então um typo geraria um SLA que casa
                                            com zero eventos sem apresentar erro nenhum. */}
                                        <ReactSelect
                                            value={TIPOS_EVENTO.find((item) => item.value === (dados.tipo ?? TODOS)) ?? null}
                                            onChange={(e) => setDados({ ...dados, tipo: e?.value ?? TODOS })}
                                            options={TIPOS_EVENTO}
                                        />
                                    </FinField>
                                    <FinField label="Complexidade">
                                        <ReactSelect
                                            value={COMPLEXIDADES.find((item) => item.value === (dados.complexidade ?? TODOS)) ?? null}
                                            onChange={(e) => setDados({ ...dados, complexidade: e?.value ?? TODOS })}
                                            options={COMPLEXIDADES}
                                        />
                                    </FinField>
                                </div>
                            </div>

                            <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                                <div className="fin-pp__cphead">
                                    <span className="fin-pp__cphead-title">Identificação</span>
                                </div>
                                <div className="fin-pp__filters">
                                    <FinField label="Descrição" required full>
                                        <FinInput
                                            id="descricao"
                                            value={dados.descricao ?? ''}
                                            onChange={(e) => setDados({ ...dados, descricao: e.target.value })}
                                            style={{ paddingLeft: 14 }}
                                        />
                                    </FinField>
                                    <FinField label="Situação">
                                        <FinCheck
                                            label="Ativo"
                                            checked={!!dados.ativo}
                                            onChange={(e) => setDados({ ...dados, ativo: e.target.checked })}
                                        />
                                    </FinField>
                                </div>
                            </div>
                        </>
                    )}

                    {tabState === 'alertas' && (
                        <div className="fin-pp__card" style={{ overflow: 'hidden', marginBottom: 16 }}>
                            <div className="fin-pp__cphead" style={{ padding: '12px 16px' }}>
                                <span className="fin-pp__cphead-ic"><FaBell size={15} /></span>
                                <span className="fin-pp__cphead-title">Alertas</span>
                                <span className="fin-pp__cphead-sub">Cada alerta tem um gatilho e uma ou mais pendências; todas precisam estar cumpridas para o alerta não disparar.</span>
                                <div style={{ marginLeft: 'auto' }}>
                                    <button
                                        type="button"
                                        className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--sm"
                                        disabled={!id}
                                        title={id ? '' : 'Salve o SLA antes de cadastrar alertas'}
                                        onClick={() => setAlertaEdicao({ ...ALERTA_VAZIO })}
                                    >
                                        <FaPlus size={12} /> Novo alerta
                                    </button>
                                </div>
                            </div>

                            <div className="fin-pp__tblwrap">
                                <table className="fin-pp__tbl" style={{ minWidth: 900, tableLayout: 'auto' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: 56 }}></th>
                                            <th style={{ width: 90 }}>Situação</th>
                                            <th>Nome</th>
                                            <th>Gatilho</th>
                                            <th style={{ width: 160 }}>Prazo</th>
                                            <th>Pendências</th>
                                            <th style={{ width: 160 }}>Destinatários</th>
                                            {/* Excluir fica na ponta oposta do alterar: separar as
                                                duas evita o clique errado numa ação sem volta. */}
                                            <th style={{ width: 56 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!id ? (
                                            <tr><td colSpan={8} className="fin-pp__empty">Salve o SLA para cadastrar alertas.</td></tr>
                                        ) : alertas.length === 0 ? (
                                            <tr><td colSpan={8} className="fin-pp__empty">Nenhum alerta</td></tr>
                                        ) : alertas.map((alerta) => (
                                            <tr key={alerta.id}>
                                                <td>
                                                    <button type="button" className="fin-pp__rowbtn" title="Alterar" onClick={() => setAlertaEdicao({ ...alerta, gatilho: idDaRelacao(alerta.gatilho), pendencias: (alerta.pendencias ?? []).map(idDaRelacao) })}>
                                                        <FaPen size={13} />
                                                    </button>
                                                </td>
                                                <td>
                                                    <span className={`fin-pp__pill ${alerta.ativo ? 'is-ok' : 'is-muted'}`}>
                                                        {alerta.ativo ? 'ativo' : 'inativo'}
                                                    </span>
                                                </td>
                                                <td className="fin-pp__cell-strong">{alerta.nome}</td>
                                                <td className="fin-pp__cell-muted">{nomeDaRelacao(alerta.gatilho) || rotuloCriterio(idDaRelacao(alerta.gatilho))}</td>
                                                <td className="fin-pp__cell-muted">{descreverPrazo(alerta)}</td>
                                                <td className="fin-pp__cell-muted">
                                                    {(alerta.pendencias ?? []).map((p) => nomeDaRelacao(p) || rotuloCriterio(idDaRelacao(p))).join(' + ') || '—'}
                                                </td>
                                                {/* d.nome vem do acessor do back: o `valor` de um destinatário 'usuario'
                                                    é um id de 18 dígitos, ilegível aqui. */}
                                                <td className="fin-pp__cell-muted">{(alerta.destinatarios ?? []).map((d) => rotuloTipoDestinatario(d.tipo) + ': ' + (d.nome || d.valor)).join(' · ') || '—'}</td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <button type="button" className="fin-pp__rowbtn" title="Excluir" onClick={() => excluirAlerta(alerta)}>
                                                        <FaTrash size={13} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </form>

                {alertaEdicao && (
                    <div
                        className="modal fade show d-block fin-pp fin-pp__modalbg"
                        onClick={(e) => { if (e.target === e.currentTarget) setAlertaEdicao(null); }}
                    >
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content fin-pp__modal">
                                <div className="fin-pp__modal-head">
                                    <span className="fin-pp__modal-head-ic"><FaBell size={17} /></span>
                                    <h3 className="fin-pp__modal-title">
                                        {alertaEdicao.id ? 'Alterar alerta' : 'Novo alerta'}
                                        <span className="fin-pp__modal-sub">{dados.descricao || 'SLA'}</span>
                                    </h3>
                                    <button type="button" className="fin-pp__modal-close" onClick={() => setAlertaEdicao(null)}>
                                        <FaTimes size={16} />
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <div className="fin-pp__filters">
                                        <FinField label="Nome" required full>
                                            <FinInput
                                                value={alertaEdicao.nome ?? ''}
                                                onChange={(e) => setAlertaEdicao({ ...alertaEdicao, nome: e.target.value })}
                                                style={{ paddingLeft: 14 }}
                                            />
                                        </FinField>

                                        <FinField label="Gatilho" required grow>
                                            {/* key força remontar quando os critérios chegam da API:
                                                [] é truthy, então o ReactSelect entra no ramo de
                                                options fixas e congelaria em "Sem opções". */}
                                            <ReactSelect
                                                key={`gatilho-${criterios.length}`}
                                                value={criteriosDe('disparo').find((item) => item.value === String(alertaEdicao.gatilho)) ?? null}
                                                onChange={(e) => setAlertaEdicao({ ...alertaEdicao, gatilho: e?.value ?? '' })}
                                                options={criteriosDe('disparo')}
                                            />
                                        </FinField>
                                        <FinField label="Dias">
                                            <FinInput
                                                type="number"
                                                value={alertaEdicao.offset_dias ?? 0}
                                                onChange={(e) => setAlertaEdicao({ ...alertaEdicao, offset_dias: e.target.value })}
                                                style={{ paddingLeft: 14 }}
                                            />
                                        </FinField>
                                        <FinField label="Contagem">
                                            <ReactSelect
                                                value={CONTAGENS.find((item) => item.value === alertaEdicao.contagem) ?? null}
                                                onChange={(e) => setAlertaEdicao({ ...alertaEdicao, contagem: e?.value ?? 'corridos' })}
                                                options={CONTAGENS}
                                            />
                                        </FinField>

                                        <FinField label="Pendências que dispensam o alerta" required full>
                                            {/* Multi: com mais de uma, TODAS precisam estar cumpridas — falta uma,
                                                o alerta continua cobrando. O e-mail explica a regra (ver texto abaixo
                                                do card). */}
                                            <ReactSelect
                                                key={`pendencias-${criterios.length}`}
                                                isMulti
                                                value={criteriosDe('pendencia').filter((item) => (alertaEdicao.pendencias ?? []).includes(item.value))}
                                                // Reconstruir só a partir dos chips visíveis PERDE pendência cujo critério
                                                // foi inativado: /slaCriterio/opcoes filtra ativo=1, então ela não vira chip
                                                // e sumiria no primeiro toque no select — silenciosamente, porque a tabela
                                                // continua listando pelo que veio da relação. Preserva os invisíveis.
                                                onChange={(e) => {
                                                    const visiveis = criteriosDe('pendencia').map((item) => item.value);
                                                    const invisiveis = (alertaEdicao.pendencias ?? []).filter((id) => !visiveis.includes(id));

                                                    setAlertaEdicao({
                                                        ...alertaEdicao,
                                                        pendencias: [...invisiveis, ...(e ?? []).map((item) => item.value)],
                                                    });
                                                }}
                                                options={criteriosDe('pendencia')}
                                            />
                                        </FinField>

                                        <FinField label="Destinatários" required full>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                {(alertaEdicao.destinatarios ?? []).map((dest, indice) => (
                                                    <div key={indice} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                        <span
                                                            className="fin-pp__pill is-info"
                                                            style={{ flex: '0 0 116px', textAlign: 'center' }}
                                                        >
                                                            {rotuloTipoDestinatario(dest.tipo)}
                                                        </span>

                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            {dest.tipo === 'email' ? (
                                                                <FinInput
                                                                    value={dest.valor ?? ''}
                                                                    placeholder="nome@empresa.com.br"
                                                                    onChange={(e) => alterarDestinatario(indice, e.target.value)}
                                                                    style={{ paddingLeft: 14 }}
                                                                />
                                                            ) : dest.tipo === 'perfil' ? (
                                                                <ReactSelect
                                                                    value={perfis.find((p) => p.value === dest.valor) ?? null}
                                                                    onChange={(e) => alterarDestinatario(indice, e?.value ?? '')}
                                                                    options={perfis}
                                                                    key={`perfil-${perfis.length}`}
                                                                />
                                                            ) : (
                                                                <ReactSelect
                                                                    value={dest.valor ? { value: dest.valor, label: dest.nomeUsuario ?? dest.nome ?? dest.valor } : null}
                                                                    onChange={(e) => alterarDestinatario(indice, e?.value ?? '', e?.label)}
                                                                    loadOptions={buscarUsuarios}
                                                                />
                                                            )}
                                                        </div>

                                                        <button
                                                            type="button"
                                                            className="fin-pp__btn fin-pp__btn--sm"
                                                            title="Remover destinatário"
                                                            onClick={() => removerDestinatario(indice)}
                                                        >
                                                            <FaTrash size={12} />
                                                        </button>
                                                    </div>
                                                ))}

                                                {/* Botão comum até o clique: só então mostra as três opções, e cada
                                                    escolha acrescenta uma linha com o controle daquele tipo. */}
                                                {menuDestinatario ? (
                                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                        {TIPOS_DESTINATARIO.map((tipo) => (
                                                            <button
                                                                key={tipo.value}
                                                                type="button"
                                                                className="fin-pp__btn fin-pp__btn--sm"
                                                                onClick={() => adicionarDestinatario(tipo.value)}
                                                            >
                                                                {tipo.label}
                                                            </button>
                                                        ))}
                                                        <button
                                                            type="button"
                                                            className="fin-pp__btn fin-pp__btn--sm"
                                                            onClick={() => setMenuDestinatario(false)}
                                                        >
                                                            <FaTimes size={12} /> Cancelar
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--pill"
                                                        style={{ alignSelf: 'flex-start' }}
                                                        onClick={() => setMenuDestinatario(true)}
                                                    >
                                                        <FaPlus size={12} /> Adicionar destinatário
                                                    </button>
                                                )}
                                            </div>
                                        </FinField>

                                        <FinField label="Texto do aviso" required full>
                                            <FinTextarea
                                                value={alertaEdicao.texto ?? ''}
                                                onChange={(e) => setAlertaEdicao({ ...alertaEdicao, texto: e.target.value })}
                                                style={{ height: 'auto', padding: '10px 14px' }}
                                            />
                                        </FinField>

                                        <FinField label="Situação">
                                            <FinCheck
                                                label="Ativo"
                                                checked={!!alertaEdicao.ativo}
                                                onChange={(e) => setAlertaEdicao({ ...alertaEdicao, ativo: e.target.checked })}
                                            />
                                        </FinField>
                                    </div>

                                    <p className="fin-pp__cphead-sub" style={{ marginTop: 4 }}>
                                        Dias negativos avisam antes do gatilho; positivos cobram depois. Zero dispara na data.
                                    </p>
                                </div>
                                <div className="fin-pp__modal-foot">
                                    <button type="button" className="fin-pp__btn" onClick={() => setAlertaEdicao(null)}>Cancelar</button>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--primary" onClick={salvarAlerta} disabled={salvandoAlerta}>
                                        {salvandoAlerta ? 'Salvando...' : 'Confirmar'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </ErpTemplate>
    );
}
